import React, { useEffect, useState } from 'react';
import logo from '../../assets/stride-logo.png';
import { Link } from 'react-router-dom';
import ManageAccountSelectBox from '../OrganizationSelectBox/ManageOrganisationSelectBox';
import { useAuth0 } from '@auth0/auth0-react';
import { useUserContext } from '../../contexts/UserContext';
import useComponentVisible from '../UI/ComponentVisible';
import ManageGlobalSelectorBox from '../GlobalSelector';
import Notification from '../Notification/Notification';
import NotificationBar from "../Notification/NotificationBar";
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { usePreviewMode } from '../../contexts/PreviewModeContext';
import { Menu, User, Building2, Users, CreditCard, LogOut } from 'lucide-react';
import { useUIContext } from '../../contexts/UIContext';

const AppNavbar = () => {
    const { logout } = useAuth0();
    const { isOwnerOfSelectedOrganization, userData } = useUserContext();
    const { enums, announcements, content } = useContentfulContext();
    const { showNavbar } = usePreviewMode();
    const { toggleMobileSidebar } = useUIContext();

    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    const [isTrialPlan, setIsTrialPlan] = useState(false);
    const handleProfileMenu = () => {
        setIsComponentVisible(!isComponentVisible);
    };

    const userInitials = (userData?.display_name || userData?.name || userData?.email || 'U')
        .slice(0, 2)
        .toUpperCase();

    useEffect(() => {
        if (!userData?.selectedOrganization) return;
        setIsTrialPlan(userData?.selectedOrganization?.level?.plan_id == announcements?.trial_plan?.trialPlanId);
    }, [userData?.selectedOrganization?.level?._id]);

    if (!showNavbar) return null;

    return (
        <>
            <NotificationBar contentfulEntry={isTrialPlan ? announcements?.trial_plan : announcements?.release} />
            <header className="sticky top-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-b border-border shadow-xs min-h-[58px]">
                <div className="w-full px-4 md:px-6">
                    <div className="flex items-center justify-between py-2 min-h-[58px]">
                        <div className="w-auto flex items-center">
                            <button
                                className="lg:hidden mr-3 p-1.5 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                                onClick={toggleMobileSidebar}
                                aria-label="Toggle Menu"
                            >
                                <Menu size={22} />
                            </button>
                            <a href="/dashboard" className="w-24 inline-block align-middle transition-opacity hover:opacity-90">
                                <img src={logo} alt="Stride Logo" />
                            </a>
                        </div>
                        <div className="w-auto flex items-center shrink-0 gap-2 md:gap-3">
                            {userData?.selectedOrganization && (
                                <div className="mr-1 ss:mr-2 relative z-[100] hidden ss:block" id="organization">
                                    <ManageAccountSelectBox />
                                </div>
                            )}
                            <div className="mr-2 ss:mr-3 relative" id="notification-wrapper">
                                <Notification id="notification" />
                            </div>
                            <div className="mr-2 ss:mr-3 relative ss:block" id="global-selector">
                                <ManageGlobalSelectorBox />
                            </div>
                            <div className="relative" ref={ref}>
                                <button
                                    id="navbar-menu"
                                    type="button"
                                    className="relative w-9 h-9 rounded-full ring-2 ring-primary/20 hover:ring-primary focus:ring-2 focus:ring-primary focus:outline-none transition-all flex items-center justify-center bg-primary-soft text-primary font-bold text-xs shrink-0 cursor-pointer overflow-hidden shadow-xs"
                                    onClick={handleProfileMenu}
                                    title={userData?.email || "Account Profile"}
                                >
                                    {userData?.picture ? (
                                        <img src={userData.picture} alt="Profile" className="w-full h-full object-cover" />
                                    ) : (
                                        <span>{userInitials}</span>
                                    )}
                                </button>
                                {isComponentVisible && (
                                    <div className="z-50 my-2 text-sm bg-white border border-border rounded-xl shadow-xl absolute top-full right-0 w-52 p-1.5 divide-y divide-slate-100 animate-in fade-in zoom-in-95 duration-100">
                                        {userData?.email && (
                                            <div className="px-3 py-2 text-xs">
                                                <p className="font-semibold text-slate-900 truncate">{userData?.display_name || userData?.name || 'User'}</p>
                                                <p className="text-slate-400 truncate text-[11px]">{userData?.email}</p>
                                            </div>
                                        )}
                                        <ul className="py-1 space-y-0.5" role="none">
                                            {userData?.selectedOrganization && (
                                                <li>
                                                    <Link
                                                        id="profile-menu-id"
                                                        to="/settings/profile"
                                                        onClick={handleProfileMenu}
                                                        className="flex items-center gap-2.5 px-3 py-2 text-slate-700 hover:bg-primary-soft/60 hover:text-primary rounded-lg transition-colors font-medium text-xs md:text-sm"
                                                        role="menuitem"
                                                    >
                                                        <User size={15} className="text-slate-400 shrink-0" />
                                                        <span>{content.APP_NAVBAR?.profile}</span>
                                                    </Link>
                                                </li>
                                            )}
                                            {userData?.selectedOrganization && (
                                                <li>
                                                    <Link
                                                        to="/settings/organisation"
                                                        onClick={handleProfileMenu}
                                                        className="flex items-center gap-2.5 px-3 py-2 text-slate-700 hover:bg-primary-soft/60 hover:text-primary rounded-lg transition-colors font-medium text-xs md:text-sm"
                                                        role="menuitem"
                                                    >
                                                        <Building2 size={15} className="text-slate-400 shrink-0" />
                                                        <span>{content.APP_NAVBAR?.organisation}</span>
                                                    </Link>
                                                </li>
                                            )}
                                            {userData?.selectedOrganization && isOwnerOfSelectedOrganization() && (
                                                <li>
                                                    <Link
                                                        id="manage-users-menu-id"
                                                        to="/settings/manage-users"
                                                        onClick={handleProfileMenu}
                                                        className="flex items-center gap-2.5 px-3 py-2 text-slate-700 hover:bg-primary-soft/60 hover:text-primary rounded-lg transition-colors font-medium text-xs md:text-sm"
                                                        role="menuitem"
                                                    >
                                                        <Users size={15} className="text-slate-400 shrink-0" />
                                                        <span>{content.APP_NAVBAR?.manage_users}</span>
                                                    </Link>
                                                </li>
                                            )}
                                            {userData?.selectedOrganization && isOwnerOfSelectedOrganization() && (
                                                <li>
                                                    <Link
                                                        id="manage-plan-menu-id"
                                                        to="/settings/my-plan"
                                                        onClick={handleProfileMenu}
                                                        className="flex items-center gap-2.5 px-3 py-2 text-slate-700 hover:bg-primary-soft/60 hover:text-primary rounded-lg transition-colors font-medium text-xs md:text-sm"
                                                        role="menuitem"
                                                    >
                                                        <CreditCard size={15} className="text-slate-400 shrink-0" />
                                                        <span>{content.OWNER_PLAN?.title}</span>
                                                    </Link>
                                                </li>
                                            )}
                                        </ul>
                                        <div className="pt-1">
                                            <a
                                                href="#"
                                                className="flex items-center gap-2.5 px-3 py-2 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors font-medium text-xs md:text-sm"
                                                role="menuitem"
                                                onClick={() => {
                                                    logout({
                                                        logoutParams: { returnTo: enums?.LOGOUT_REDIRECT_URL },
                                                    });
                                                }}
                                            >
                                                <LogOut size={15} className="shrink-0" />
                                                <span>{content.APP_NAVBAR?.logout}</span>
                                            </a>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
};

export default AppNavbar;